import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import TutorialButton from './common/TutorialButton';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/acompanhamento.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const AcompanhamentoManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_acompanhamento: '',
    id_solicitacao: '',
    data_vistoria: '',
    itens_avaliados: '',
    id_imagem: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [imagens, setImagens] = useState([]);
  const [imagemSearch, setImagemSearch] = useState('');
  const [camposComErro, setCamposComErro] = useState([]); 

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  // Filtrar imagens com base na busca
  const imagensFiltradas = useMemo(() => {
    if (!imagemSearch) return imagens;
    return imagens.filter(img =>
      img.descricao?.toLowerCase().includes(imagemSearch.toLowerCase()) ||
      img.id_imagem?.toString().includes(imagemSearch)
    );
  }, [imagens, imagemSearch]);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchSolicitacoes = useCallback(async () => {
    try {
      const response = await api.get('/solicitacao');
      setSolicitacoes(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar solicitações: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchImagens = useCallback(async () => {
    try {
      const response = await api.get('/imagem');
      setImagens(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar imagens: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/acompanhamento');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      mostrarMensagem('error', `Erro de conexão: ${error.message}`);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchSolicitacoes();
    fetchImagens();
  }, [verificarAPI, fetchSolicitacoes, fetchImagens]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/acompanhamento');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'acompanhamento', { id, dadosAtualizacao });
    try {
      const response = await api.put(`/acompanhamento/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'acompanhamento', response.data);
      if (response && response.data.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
        carregarDados(false);
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'acompanhamento');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem, carregarDados]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'acompanhamento', { id });
    try {
      const response = await api.delete(`/acompanhamento/${id}`);
      logarSucesso('Excluir', 'acompanhamento', response.data);
      setDados(currentData => currentData.filter(item => item.id_acompanhamento !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'acompanhamento');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const colunas = useMemo(() => {
    const solicitacoesNomes = solicitacoes.reduce((acc, item) => ({ ...acc, [item.id_solicitacao]: item.descr_solicitacao }), {});
    const imagensNomes = imagens.reduce((acc, item) => ({ ...acc, [item.id_imagem]: item.descricao }), {});

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_acompanhamento, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_acompanhamento}"?`)) {
            excluirRegistro(data.id_acompanhamento);
          }
        },
        headerSort: false
      },
      {
        title: 'ID Acompanhamento',
        field: 'id_acompanhamento',
        width: 220,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Solicitação',
        field: 'id_solicitacao',
        width: 250,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Solicitação...',
        sorter: 'string',
        formatter: (cell) => solicitacoesNomes[cell.getValue()] || cell.getValue(),
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeSolicitacao = solicitacoesNomes[rowValue] || rowValue || '';
          return nomeSolicitacao.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: { values: solicitacoesNomes }
      },
      {
        title: 'Data Vistoria',
        field: 'data_vistoria',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar data...',
        sorter: 'date',
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          if (!rowValue) return false;

          try {
            const date = new Date(rowValue);
            if (isNaN(date.getTime())) return rowValue.includes(headerValue);

            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = String(date.getUTCMonth() + 1).padStart(2, '0');
            const year = date.getUTCFullYear();
            const formattedDate = `${day}/${month}/${year}`;

            return formattedDate.includes(headerValue);
          } catch (e) {
            return false;
          }
        },
        formatter: (cell) => {
          const value = cell.getValue();
          if (!value) return '';

          // Se já estiver em formato DD/MM/AAAA, retorna direto
          if (value.includes('/')) return value;

          // Converte de AAAA-MM-DD para DD/MM/AAAA
          const date = new Date(value + 'T00:00:00Z');
          if (isNaN(date.getTime())) return value;

          const day = String(date.getUTCDate()).padStart(2, '0');
          const month = String(date.getUTCMonth() + 1).padStart(2, '0');
          const year = date.getUTCFullYear();
          return `${day}/${month}/${year}`;
        },
        editor: (cell, onRendered, success, cancel) => {
          const input = document.createElement("input");
          input.setAttribute("type", "text");
          input.setAttribute("placeholder", "DD/MM/YYYY");
          input.style.padding = "4px";
          input.style.width = "100%";
          input.style.boxSizing = "border-box";

          const value = cell.getValue();
          if (value) {
            try {
              const date = new Date(value);
              if (!isNaN(date.getTime())) {
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const year = date.getUTCFullYear();
                input.value = `${day}/${month}/${year}`;
              } else {
                input.value = value;
              }
            } catch (e) {
              input.value = value;
            }
          }

          onRendered(() => {
            input.focus();
            input.style.height = "100%";
          });

          const handleDateInput = (e) => {
            const input = e.target;
            let value = input.value.replace(/\D/g, '');
            let formattedValue = '';
            if (value.length > 0) {
              formattedValue = value.substring(0, 2);
            }
            if (value.length > 2) {
              formattedValue += '/' + value.substring(2, 4);
            }
            if (value.length > 4) {
              formattedValue += '/' + value.substring(4, 8);
            }
            input.value = formattedValue;
          };

          input.addEventListener('input', handleDateInput);

          const onChange = () => {
            if (input.value && input.value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const [day, month, year] = input.value.split('/');
              success(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`);
            } else {
              success(input.value);
            }
          };

          input.addEventListener("change", onChange);
          input.addEventListener("blur", onChange);

          return input;
        }
      },
      {
        title: 'Itens Avaliados',
        field: 'itens_avaliados',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar itens...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Imagem',
        field: 'id_imagem',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar Imagem...',
        sorter: 'string',
        formatter: (cell) => imagensNomes[cell.getValue()] || cell.getValue(),
        headerFilterFunc: (headerValue, rowValue, rowData, filterParams) => {
          if (!headerValue) return true;
          const nomeImagem = imagensNomes[rowValue] || rowValue || '';
          return nomeImagem.toLowerCase().includes(headerValue.toLowerCase());
        },
        editor: 'list',
        editorParams: { values: imagensNomes }
      }
    ];
  }, [solicitacoes, imagens, atualizarRegistro, excluirRegistro]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitColumns',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_acompanhamento'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(colunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, colunas, inicializarTabulator]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_acompanhamento, id_solicitacao, data_vistoria, itens_avaliados, id_imagem } = formData;

    const camposFaltando = [];
    if (!id_acompanhamento) camposFaltando.push('ID Acompanhamento');
    if (!id_solicitacao) camposFaltando.push('Solicitação');

    if (camposFaltando.length > 0) {
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposFaltando.join(', ')}`);
      return;
    }

    let dataVistoriaISO = null;
    if (data_vistoria && data_vistoria.length === 10) {
      const [day, month, year] = data_vistoria.split('/');
      dataVistoriaISO = `${year}-${month}-${day}`;
    }

    const dataToSend = {
      id_acompanhamento,
      id_solicitacao,
      data_vistoria: dataVistoriaISO,
      itens_avaliados,
      id_imagem
    };

    logarRequisicao('Adicionar', 'acompanhamento', dataToSend);
    try {
      setLoading(true);
      const response = await api.post('/acompanhamento', dataToSend);
      logarSucesso('Adicionar', 'acompanhamento', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_acompanhamento: '', id_solicitacao: '', data_vistoria: '', itens_avaliados: '', id_imagem: '' });
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'acompanhamento');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDataVistoriaChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');

    if (value.length <= 2) {
      e.target.value = value;
    } else if (value.length <= 4) {
      e.target.value = `${value.slice(0, 2)}/${value.slice(2)}`;
    } else if (value.length <= 8) {
      e.target.value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;
    } else {
      e.target.value = `${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`;
    }

    setFormData(prev => ({ ...prev, data_vistoria: e.target.value }));
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'acompanhamento.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Acompanhamento</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_acompanhamento</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/acompanhamento.mov" buttonText="Ver Tutorial Acompanhamento" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_acompanhamento">ID Acompanhamento:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input type="text" id="id_acompanhamento" name="id_acompanhamento" value={formData.id_acompanhamento} onChange={handleInputChange} placeholder="Ex: 1" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="id_solicitacao">Solicitação:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <select id="id_solicitacao" name="id_solicitacao" value={formData.id_solicitacao} onChange={handleInputChange} disabled={loading || apiStatus !== 'connected'}>
              <option value="">Selecione uma solicitação</option>
              {solicitacoes.map(item => (<option key={item.id_solicitacao} value={item.id_solicitacao}>{item.descr_solicitacao}</option>))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="data_vistoria">Data vistoria:</label>
            <input type="text" id="data_vistoria" name="data_vistoria" value={formData.data_vistoria} onChange={handleDataVistoriaChange} placeholder="DD/MM/AAAA" maxLength="10" disabled={loading || apiStatus !== 'connected'} className={camposComErro.includes('data_vistoria') ? 'input-error' : ''} />
          </div>
          <div className="form-group">
            <label htmlFor="itens_avaliados">Itens avaliados:</label>
            <input type="text" id="itens_avaliados" name="itens_avaliados" value={formData.itens_avaliados} onChange={handleInputChange} placeholder="Ex: Item 1, Item 2" disabled={loading || apiStatus !== 'connected'} />
          </div>
          <div className="form-group">
            <label htmlFor="id_imagem">Imagem:</label>
            <input
              type="text"
              id="id_imagem"
              name="id_imagem"
              value={imagemSearch}
              onChange={(e) => {
                setImagemSearch(e.target.value);
                const imagemEncontrada = imagens.find(img =>
                  img.descricao === e.target.value
                );
                if (imagemEncontrada) {
                  setFormData(prev => ({ ...prev, id_imagem: imagemEncontrada.id_imagem }));
                } else {
                  setFormData(prev => ({ ...prev, id_imagem: '' }));
                }
              }}
              list="imagens-list"
              placeholder="Digite para buscar imagem..."
              disabled={loading || apiStatus !== 'connected'}
              autoComplete="off"
            />
            <datalist id="imagens-list">
              {imagensFiltradas.map(img => (
                <option key={img.id_imagem} value={img.descricao} />
              ))}
            </datalist>
          </div>

          <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
            {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
          </button>
          <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
            {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
          </button>
          <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>

          <div className="info-campos-obrigatorios">
            <i className="fas fa-info-circle"></i>
            <span>* indica que os campos são obrigatorios.</span>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default AcompanhamentoManager;
